import { useSearchParams } from 'next/navigation'
import { useRouter } from 'next/router'
import { StringifiableRecord, stringifyUrl } from 'query-string'

import { createPdpBaseUrl } from '../../app/_components/navigation/createPdpUrl'
import { createReviewsPageUrl } from '../../app/_components/navigation/createReviewsPageUrl'
import { Product } from '../../types/graphql'
import { optionalLocaleURLRegex } from '../server-middleware/expressMiddlewareUtils'

export function collectionHref(handle: string, query: StringifiableRecord = {}) {
  return stringifyUrl({
    url: `/collections/${handle}`,
    query,
  })
}

/**
 * Strips locale prefix from pathname if present, otherwise returns the original pathname.
 * Handles URLs like /es-MX/collections/... by returning /collections/...
 */
export function stripLocaleFromPathname(pathname: string): string {
  const matches = pathname.match(optionalLocaleURLRegex)
  return matches?.[1] ? matches?.[2] ?? '/' : pathname
}

/**
 * @deprecated Use urlPath from ProductCatalog graphql response data.
 */
export const useProductOrKitHref = (item: Product) => {
  const category = item.rawCategory
  const productGroupHandle = item.productGroup.handle
  const colorName = item.colorInfo?.name

  const isKit = category === 'Product Kit'

  const query: StringifiableRecord = {
    color: colorName,
  }

  const kitHref = useKitHref(productGroupHandle, query)
  const productHref = useProductHref(productGroupHandle, query)
  const pdpHref = isKit ? kitHref : productHref

  return pdpHref
}

/**
 * @deprecated Use urlPath from ProductCatalog graphql response data.
 */
export function useKitHref(handle: string, query: StringifiableRecord = {}) {
  return stringifyUrl({
    url: `/kits/${handle}`,
    query,
  })
}

/**
 * @deprecated Use urlPath from ProductCatalog graphql response data.
 */
type ProductForHref = {
  productGroupHandle: string
  exclusive: boolean
  genderCategory: string
}

/**
 * @deprecated Use urlPath from ProductCatalog graphql response data.
 */
export function useProductHref(productGroupHandle: string, query: StringifiableRecord): string

/**
 * @deprecated Use urlPath from ProductCatalog graphql response data.
 */
export function useProductHref(product: ProductForHref, query: StringifiableRecord): string

/**
 * @deprecated Use urlPath from ProductCatalog graphql response data.
 */
export function useProductHref(
  input: string | ProductForHref,
  query: StringifiableRecord = {}
): string {
  const router = useRouter()
  const productGroupHandle = typeof input === 'string' ? input : input.productGroupHandle

  const pathParamHandle: string | undefined = router?.query.handle
    ? `${router.query.handle}`
    : undefined

  // if the URL is being requested in the context of a specific collection, scope the product to that collection
  const collectionHandle: string | undefined =
    router?.pathname === '/collections/[handle]' ? pathParamHandle : undefined

  const isExclusive: boolean = typeof input === 'object' ? input.exclusive : false
  const genderKey: string = typeof input === 'object' ? input.genderCategory : 'women'

  return stringifyUrl({
    url: createPdpBaseUrl({
      productGroupHandle,
      collectionHandle,
      isKit: false,
      isExclusive,
      genderKey,
    }),
    query,
  })
}

/**
 * Generates a product href that preserves collection context when on collection product pages.
 * Specifically designed for use cases like style links where you want to stay within the same collection.
 */
/**
 * @deprecated Use urlPath from ProductCatalog graphql response data.
 */
export function useCollectionPersistentProductHref(
  productGroupHandle: string,
  query: StringifiableRecord = {}
): string {
  const router = useRouter()

  const pathnameWithoutLocale = stripLocaleFromPathname(router?.pathname || '')
  const isOnCollectionProductPage =
    pathnameWithoutLocale === '/collections/[handle]/products/[productGroupHandle]'

  const collectionHandle =
    isOnCollectionProductPage && router?.query.handle ? `${router.query.handle}` : undefined

  return stringifyUrl({
    url: createPdpBaseUrl({
      productGroupHandle,
      collectionHandle,
      isKit: false,
      isExclusive: false,
      genderKey: null,
    }),
    query,
  })
}

export function useProductReviewsHref({
  handle,
  colorKey,
  query = {},
}: {
  handle: string
  query?: StringifiableRecord
  colorKey: string | undefined | null
}) {
  return stringifyUrl({
    url: createReviewsPageUrl({
      colorKey,
      handle,
      type: 'read-review',
    }),
    query,
  })
}

export function useProductWriteReviewHref({
  handle,
  colorKey,
  query = {},
}: {
  handle: string
  query?: StringifiableRecord
  colorKey: string | undefined | null
}) {
  return stringifyUrl({
    url: createReviewsPageUrl({
      colorKey,
      handle,
      type: 'write-review',
    }),
    query,
  })
}

export function portalCollectionHref(handle: string, query: StringifiableRecord = {}) {
  return stringifyUrl({
    url: `/portals/${handle}`,
    query,
  })
}

export function giftProductHref(
  handle: string,
  slug: string,
  giftItemCollectionId: string,
  collectionHandle: string,
  query: StringifiableRecord = {}
) {
  return stringifyUrl({
    url: `/claim-gift/${slug}/collection/${giftItemCollectionId}/${collectionHandle}/product/${handle}`,
    query,
  })
}

export function giftCollectionHref(
  giftItemCollectionId: string,
  collectionHandle: string,
  slug: string,
  query: StringifiableRecord = {}
) {
  return stringifyUrl({
    url: `/claim-gift/${slug}/collection/${giftItemCollectionId}/${collectionHandle}`,
    query,
  })
}

/**
 * Not intended for boolean false param value, since we always return a string.
 * A false param value should return null, but that would ruin the convenience
 * of this convenience function.
 */
export function singleQueryParamValue(
  queryParamValue: string | string[] | null | undefined,
  defaultValue: string = ''
): string {
  if (typeof queryParamValue === 'boolean') return ''
  return (Array.isArray(queryParamValue) ? queryParamValue[0] : queryParamValue) || defaultValue
}

export function useProductHrefWithQuery({ color, handle }: { color: string; handle: string }) {
  // plain=true query param is used by iOS app to view webpages within a webview without the website's header/footer
  const plainQueryParamValue = useSearchParams()?.get('plain') ?? undefined

  const query = { color, plain: plainQueryParamValue || undefined }
  return useProductHref(handle, query)
}
